/** The onboarding questionnaire: two skippable questions a new account
 *  answers once, on the first page it lands on. Who they are, and what they
 *  came to make first.
 *  The answers are recorded and land on the PostHog person, so cohorts are
 *  one filter; they change nothing else in the product. */

export const ONBOARDING_ROLES = ['designer', 'engineer', 'vibe_coder', 'founder', 'marketer', 'exploring'] as const
export type OnboardingRole = (typeof ONBOARDING_ROLES)[number]

export const ONBOARDING_INTENTS = ['landing_page', 'product_ui', 'prototype', 'social', 'email', 'unsure'] as const
export type OnboardingIntent = (typeof ONBOARDING_INTENTS)[number]

export const ONBOARDING_STEPS = ['role', 'intent'] as const
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number]

/** One question's answer is `null` when that step was skipped. */
export interface OnboardingAnswers {
  role: OnboardingRole | null
  intent: OnboardingIntent | null
}

export interface OnboardingProfile extends OnboardingAnswers {
  completedAt: number
}

export const EMPTY_ONBOARDING_ANSWERS: OnboardingAnswers = { role: null, intent: null }

const isRole = (value: unknown): value is OnboardingRole => ONBOARDING_ROLES.includes(value as OnboardingRole)
const isIntent = (value: unknown): value is OnboardingIntent => ONBOARDING_INTENTS.includes(value as OnboardingIntent)

/** The request body as answers, or `null` when it is not one. A skipped
 *  question may arrive as `null` or be left out; an unknown value anywhere
 *  refuses the whole body rather than silently dropping it. */
export function parseOnboardingAnswers(input: unknown): OnboardingAnswers | null {
  if (!input || typeof input !== 'object') return null
  const { role = null, intent = null } = input as Record<string, unknown>
  if (role !== null && !isRole(role)) return null
  if (intent !== null && !isIntent(intent)) return null
  return { role, intent }
}

/** The steps left unanswered, for the analytics event. */
export function skippedOnboardingSteps(answers: OnboardingAnswers): OnboardingStep[] {
  const skipped: OnboardingStep[] = []
  if (answers.role === null) skipped.push('role')
  if (answers.intent === null) skipped.push('intent')
  return skipped
}
